import { supabase } from '../supabase.ts';

/**
 * The workspace record, its owner, the signed-in user's own profile, and the
 * plan.
 *
 * `accounts` is gated by `accounts_self_update`, which admits the primary
 * owner alone -- so a rename by anyone else is refused by the database, and
 * this module does not pretend otherwise.
 */

export interface AccountRecord {
  id: string;
  name: string;
  slug: string | null;
  pictureUrl: string | null;
  primaryOwnerUserId: string;
}

export async function getAccount(accountId: string): Promise<AccountRecord | null> {
  const { data, error } = await supabase
    .from('accounts')
    .select('id, name, slug, picture_url, primary_owner_user_id')
    .eq('id', accountId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    name: data.name,
    slug: data.slug,
    pictureUrl: data.picture_url,
    primaryOwnerUserId: data.primary_owner_user_id,
  };
}

export async function saveAccount(
  accountId: string,
  input: { name: string; pictureUrl: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('accounts')
    .update({ name: input.name, picture_url: input.pictureUrl })
    .eq('id', accountId);

  if (error) throw error;
}

export interface OwnerCandidate {
  userId: string;
  name: string;
  email: string | null;
  role: string;
}

/**
 * Members who could hold the account, for the transfer picker.
 *
 * Names come off the membership itself rather than `auth.users`, which the
 * console cannot read: a staff member's name and badge live on the row that
 * grants them access.
 */
export async function listOwnerCandidates(accountId: string): Promise<OwnerCandidate[]> {
  const { data, error } = await supabase
    .from('accounts_memberships')
    .select('user_id, account_role, first_name, last_name, badge_number')
    .eq('account_id', accountId);

  if (error) throw error;

  return (data ?? []).map((row) => ({
    userId: row.user_id,
    name:
      [row.first_name, row.last_name].filter(Boolean).join(' ') ||
      row.badge_number ||
      row.user_id.slice(0, 8),
    email: null,
    role: row.account_role,
  }));
}

/**
 * Hand the workspace to somebody else.
 *
 * `transfer_team_account_ownership` does the swap in one transaction: the new
 * owner is promoted and the old one demoted, so there is never a moment with
 * two primary owners or none.
 */
export async function transferOwnership(accountId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc('transfer_team_account_ownership', {
    target_account_id: accountId,
    new_owner_id: userId,
  });

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* The signed-in user                                                          */
/* -------------------------------------------------------------------------- */

export async function updateProfile(input: { name: string; email: string }): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    email: input.email,
    data: { name: input.name },
  });

  if (error) throw error;
}

export async function updatePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });

  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/* Billing                                                                     */
/* -------------------------------------------------------------------------- */

export interface SubscriptionRecord {
  status: string;
  active: boolean;
  provider: string | null;
  periodEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
}

export async function getSubscription(accountId: string): Promise<SubscriptionRecord | null> {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('status, active, billing_provider, period_ends_at, cancel_at_period_end')
    .eq('account_id', accountId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    status: data.status,
    active: data.active,
    provider: data.billing_provider,
    periodEndsAt: data.period_ends_at,
    cancelAtPeriodEnd: data.cancel_at_period_end,
  };
}
